import { createTelemetry, createTelemetryObject } from '@shared/core/analytics';
import { PageFunctionType } from '@shared/core/analytics/telemetry/types';
import { PayoutAccountInfo } from './CreditCardSetUp.context';
import { ApolloError } from '@apollo/client';

const pagePrefix = 'admin.registry';

export type PagesForTelemetry = 'creditCardSetupDialog' | 'payoutDialog';

const pages: { [tab in PagesForTelemetry]: PageFunctionType } = {
  creditCardSetupDialog: (status: string) => ({
    category: 'joyCredit',
    pagePrefix,
    page: 'CreditCardOnboardingDialog.' + status,
  }),
  payoutDialog: (status: string) => ({
    category: 'joyCredit',
    pagePrefix,
    page: 'PayoutDialog.' + status,
  }),
};

export const creditCardSetupTelemetry = createTelemetryObject({
  actions: {
    getStartedCTAButton: () => ({
      pagePrefix,
      category: 'track.joyCredit',
      action: 'ButtonInteracted',
      label: 'Get Started',
      extraInfo: {
        name: 'OpenCreditCardOnboardingDialog',
      },
    }),
    toggleCreditCardOption: () => ({
      pagePrefix,
      category: 'addCashFund.paymentMethod',
      action: 'ButtonInteracted',
      label: 'Credit Card Toggle',
      extraInfo: {
        name: 'OpenCreditCardOnboardingDialog',
      },
    }),
    letsGetsStartedButtonClicked: () => ({
      pagePrefix,
      category: 'joyCredit.dialog',
      action: 'ButtonInteracted',
      label: `Let's Get Started`,
      extraInfo: {
        name: 'LaunchStripeOnboarding',
      },
    }),
    transferBalanceButtonClicked: (balance: number, currency: string, fee: number) => ({
      pagePrefix,
      category: 'payout.dialog',
      action: 'ButtonInteracted',
      label: 'Yes, Transfer Balance',
      extraInfo: {
        name: 'TransferBalance',
        balance,
        currency,
        fee,
      },
    }),
    nevermindButtonClicked: () => ({
      pagePrefix,
      category: 'payout.dialog',
      action: 'ButtonInteracted',
      label: 'Nevermind',
      extraInfo: {
        name: 'ClosePayoutDialog',
      },
    }),
    transferBalanceWidgetButtonClicked: () => ({
      pagePrefix,
      category: 'joyCredit.widget',
      action: 'ButtonInteracted',
      label: 'Transfer Balance',
      extraInfo: {
        name: 'OpenPayoutDialog',
      },
    }),
    accountButtonClicked: (url: string) => ({
      pagePrefix,
      category: 'joyCredit.widget',
      action: 'ButtonInteracted',
      label: 'Account',
      extraInfo: {
        name: 'OpenCustomerStripeConnectDashboard',
        url,
      },
    }),
    closePayoutDialogStatusButtonClicked: (status: string) => ({
      pagePrefix,
      category: 'joyCredit.widget',
      action: 'ButtonInteracted',
      label: 'Close',
      extraInfo: {
        name: 'ClosePayoutTransferStatusDialog',
        status,
      },
    }),
    resolveStripeIssueButtonClicked: (category: string, url: string) => ({
      pagePrefix,
      category,
      action: 'ButtonInteracted',
      label: 'Resolve Stripe Issue',
      extraInfo: {
        name: 'ResolveStripeIssue',
        url,
      },
    }),
    checkStripeStatusClicked: (category: string, url) => ({
      pagePrefix,
      category,
      action: 'ButtonInteracted',
      label: 'Check Stripe Status',
      extraInfo: {
        name: 'CheckStripeStatus',
        url,
      },
    }),
    createCashFundButtonClicked: () => ({
      pagePrefix,
      category: 'joyCredit.dialog',
      action: 'ButtonInteracted',
      label: 'Create Cash Fund',
      extraInfo: {
        name: 'CreateCashFundFromCrediCardOnboardingSuccessDialog',
      },
    }),
    illDoLaterButtonClicked: () => ({
      pagePrefix,
      category: 'joyCredit.dialog',
      action: 'ButtonInteracted',
      label: `I'll do it later`,
      extraInfo: {
        name: 'CloseCrediCardOnboardingSuccessDialog',
      },
    }),
    initHostFormSubmitted: () => ({
      pagePrefix,
      category: 'joyCredit.dialog',
      action: 'ButtonInteracted',
      label: 'Continue',
      extraInfo: {
        name: 'InitHostFormSubmitted',
      },
    }),
    kycContinueButtonClicked: (payoutInfo: PayoutAccountInfo) => ({
      pagePrefix,
      category: 'joyCredit.dialog',
      action: 'ButtonInteracted',
      label: 'Continue',
      extraInfo: {
        payoutInfo,
      },
    }),
    // Error tracking
    trackError: (mutation: string, error: ApolloError | Error, extra = {}) => ({
      category: 'error.joyCredit',
      action: 'Mutation',
      actionType: 'error',
      extraInfo: { error, mutation, ...extra },
    }),
  },
  pages,
});

export const { TelemetryProvider, useTelemetry: useCreditCardSetupTelemetry } = createTelemetry(creditCardSetupTelemetry, { eventId: '' });
